import React, { useEffect, useReducer } from "react";
import U2000DetailTable from "../../../../../u2000Core/components/tables/detail/U2000DetailTable";
import { useTranslation } from "react-i18next";
import U2000ActionBtn from "../../../../../u2000Core/components/buttons/U2000ActionBtn";
import { NS, U3143ROLES } from "../../../../U3143";
import { U2000IdCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdCheckedBoxDisplay";
import { U2000IdHeaderCheckedBoxDisplay } from "../../../../../u2000Core/components/tables/display/U2000IdHeaderCheckedBoxDisplay";
import { FaPlus, FaRegFile, FaTrashAlt } from "react-icons/fa";
import U2000ResponsiveToolbar from "../../../../../u2000Core/components/toolbar/U2000ResponsiveToolbar";

import { useU2000AuthContext } from "../../../../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000GridStateHandlers } from "../../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import type { TFunction } from "i18next";
import type { U2000TableColumn } from "../../../../../u2000Core/components/tables/basic/U2000BasicTableList";
import u2000FormState, { U2000FormStateEnumState, type U2000FormState } from "../../../../../u2000Core/reducer/formReducer/U2000FormState";
import U3143CadastreEditDlg from "./U3143CadastreEditDlg";
import { emptyCadastre, type Cadastre } from "../../../../U3143Service";
import U3143CadastreDeleteDlg from "./U3143CadastreDeleteDlg";
import type { U3143DistrictCadastreFormExtendedHandlers } from "../../../U3143DistrictCadastreForm";
import { u3143CadastreHandler } from "./U3143CadastreHandlers";
import { u2000IntFormState, useU2000FormStateHandlers, type U2000FormSBusinessHandlers, type U2000FormStateHandlersReturn } from "../../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";

export interface U3143CadastreFormExtendedHandlers extends U2000FormStateHandlersReturn<Cadastre> {}

// Typage des props du composant
interface U3143CadastreComponentProps {
  u3143DistrictCadastreformStateExtendedHandlers: U3143DistrictCadastreFormExtendedHandlers;
}

// Définition des colonnes pour le tableau des cadastres d'un district cadastral
function getColumnsDef(t: TFunction): U2000TableColumn[] {
  return [
    {
      name: "id",
      label: "",
      widthClass: "w-[50px]",
      type: "number",
      align: "center",
      headerDisplay: (column: any, label: string, allRowSelected: boolean, handleSelectAllRow?: (isSelected: boolean) => void) => {
        return <U2000IdHeaderCheckedBoxDisplay column={column} label={label} disabled={false} allRowSelected={allRowSelected} handleSelectAllRow={handleSelectAllRow} />;
      },
      display: (column: any, value: any, row: any, allowRowSelection: boolean, rowUpdated?: (row: any) => void) => {
        return <U2000IdCheckedBoxDisplay column={column} value={value} row={row} disabled={false} rowUpdated={rowUpdated} allowRowSelection={allowRowSelection} />;
      },
      isCriteriaField: false,
    },
    {
      name: "cadastreCode",
      label: t("lblCadastreCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      isDefaultSortedColumn: true,
      widthClass: "w-[200px]",
      type: "string",
      align: "left",
    },
    {
      name: "cadastre",
      label: t("lblCadastre", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
    {
      name: "rankCode",
      label: t("lblRankCode", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      widthClass: "w-[150px]",
      type: "string",
      align: "left",
    },
    {
      name: "rankLabel",
      label: t("lblRankLabel", { ns: NS }),
      inGridField: true,
      sortable: true,
      isDefaultColumn: true,
      type: "string",
      align: "left",
    },
  ];
}

// Composant principal pour la gestion des cadastres d'un district cadastral
const U3143CadastreBody: React.FC<U3143CadastreComponentProps> = ({ u3143DistrictCadastreformStateExtendedHandlers }) => {
  // Translation
  const { t } = useTranslation();
  const { hasRole } = useU2000AuthContext();

  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const gridStateHandlers = useU2000GridStateHandlers<Cadastre>();

  const validateCadastre = (cadastre: Cadastre) => {
    const result: Record<string, string> = {};
    if (!cadastre.cadastreCode) result.cadastreCode = t("errCadastreCodeRequired", { ns: NS });
    if (!cadastre.cadastre) {
      result.cadastre = t("errCadastreRequired", { ns: NS });
    }
    if (!cadastre.rankCode) {
      result.rankCode = t("errRankCodeRequired", { ns: NS });
    }
    if (!cadastre.rankLabel) {
      result.rankLabel = t("errRankLabelRequired", { ns: NS });
    }

    // no duplicate Cadastres (by cadastreCode)
    const duplicate = gridStateHandlers.getData().find((item) => item.cadastreCode === cadastre.cadastreCode && item.id !== cadastre.id);
    if (duplicate) {
      result.cadastreCode = t("errCadastreDuplicate", { ns: NS });
    }

    return result;
  };

  const u3143CadastreBL: U2000FormSBusinessHandlers<Cadastre> = {
    newRecord: () => {
      return emptyCadastre;
    },

    // validation du formulaire
    validateForm: validateCadastre,
  };

  const u3143CadastreformStateHandlers = useU2000FormStateHandlers<Cadastre>({
    formState,
    formStateDispatch,
    businessHandlers: u3143CadastreBL,
  });

  const u3143CadastreHandlers = u3143CadastreHandler({ gridStateHandlers, t, formStateHandlers: u3143CadastreformStateHandlers });

  // Transposer les données du formStateHandlers au gridStateHandlers pour Cadastres
  useEffect(() => {
    // Initialiser les colonnes et les données du tableau
    const columns = getColumnsDef(t);
    gridStateHandlers.setColumns(columns);
    gridStateHandlers.setSelectedFields(columns.filter((col) => col.isDefaultColumn).map((col) => col.name));
    gridStateHandlers.setSelectedSortedFields(columns.filter((col) => col.isDefaultSortedColumn).map((col) => ({ name: col.name, sortOrder: "ASC" })));

    // Initialiser les données du tableau
    const cadastres = u3143DistrictCadastreformStateExtendedHandlers.getData?.()?.cadastres;
    gridStateHandlers.setData(Array.isArray(cadastres) ? cadastres : []);
    gridStateHandlers.setTotalRows(Array.isArray(cadastres) ? cadastres.length : 0);

    // Gérer les erreurs spécifiques au champ Cadastres
    const errorsField = u3143DistrictCadastreformStateExtendedHandlers.getOtherErrors()?.cadastres;
    if (errorsField !== undefined) {
      gridStateHandlers.setErrors(Array.isArray(errorsField) ? errorsField : errorsField ? [errorsField] : []);
    } else {
      gridStateHandlers.setErrors([]);
    }
  }, [u3143DistrictCadastreformStateExtendedHandlers, t]);

  // Mettre à jour le formStateHandlers parent lorsque les données du gridStateHandlers changent
  useEffect(() => {
    if (u3143CadastreHandlers.dataChangedEvent === 0) return;

    const cadastres = gridStateHandlers.getData();
    u3143DistrictCadastreformStateExtendedHandlers.setData({
      ...u3143DistrictCadastreformStateExtendedHandlers.getData?.(),
      cadastres: cadastres,
    });
  }, [u3143CadastreHandlers.dataChangedEvent]);

  // Désactiver le bouton de modification si aucun ou plus d'une ligne sélectionnée
  function disabledUpdateBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3143DistrictCadastreformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length !== 1 ? true : false;
  }

  // Désactiver le bouton de suppression si aucune ligne sélectionnée
  function disableDeleteBtn(): boolean {
    const selectedRows = gridStateHandlers.getSelectedRows();
    return u3143DistrictCadastreformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ || selectedRows.length === 0 ? true : false;
  }

  // Préparer l'array d'actions pour le toolbar responsive
  const actions = React.useMemo(
    () => [
      {
        key: "add",
        label: t("btnAdd", { ns: NS }),
        handleClick: u3143CadastreHandlers.onAdd,
        hasRole: hasRole(U3143ROLES.EDIT),
        disabled: u3143DistrictCadastreformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.READ,
        icon: FaPlus,
        Component: U2000ActionBtn,
      },
      {
        key: "modify",
        label: t("btnModify", { ns: NS }),
        handleClick: u3143CadastreHandlers.onModify,
        hasRole: hasRole(U3143ROLES.EDIT),
        disabled: disabledUpdateBtn(),
        icon: FaRegFile,
        Component: U2000ActionBtn,
      },
      {
        key: "delete",
        label: t("btnDelete", { ns: NS }),
        handleClick: u3143CadastreHandlers.onDelete,
        hasRole: hasRole(U3143ROLES.EDIT),
        disabled: disableDeleteBtn(),
        icon: FaTrashAlt,
        Component: U2000ActionBtn,
      },
    ],
    [t, u3143CadastreHandlers]
  );

  // Render
  return (
    <>
      <U3143CadastreEditDlg u3143CadastreHandler={u3143CadastreHandlers} t={t} NS={NS} />
      <U3143CadastreDeleteDlg u3143CadastreHandler={u3143CadastreHandlers} t={t} NS={NS} />

      <div className="mt-1 mb-1 flex items-center gap-x-2 border-b-2 pb-3">
        <U2000ResponsiveToolbar actions={actions} />
      </div>
      <U2000DetailTable
        gridStateHandlers={gridStateHandlers}
        allowRowSelection={u3143DistrictCadastreformStateExtendedHandlers.getFormState?.() === U2000FormStateEnumState.EDIT}
        label={t("lblCadastre", {
          count: u3143CadastreHandlers.gridStateHandlers.getTotalRows(),
          ns: NS,
        })}
      />
    </>
  );
};

export default U3143CadastreBody;
